import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import site from "../config/site.json";
import { LANGUAGES, langFromPath, localizedPath, stripLang } from "./lang";

// ─── Head tags per URL ────────────────────────────────────────────────────────
// public/index.html carries the English defaults. This rewrites them for the
// language in the URL, and react-snap saves the result into each prerendered
// page, so crawlers that never run JavaScript still get the right title,
// description, canonical and hreflang set. Identical in all five DRIVE apps;
// the per-app values live in config/site.json and the "seo" translation keys.

function setMeta(attr: "name" | "property" | "itemprop", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeAll(selector: string) {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

function addLink(rel: string, href: string, hreflang?: string) {
  const el = document.createElement("link");
  el.rel = rel;
  el.href = href;
  if (hreflang) el.hreflang = hreflang;
  document.head.appendChild(el);
}

export function SeoHead() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  const page = stripLang(pathname);
  const indexable = site.routes.includes(page);

  useEffect(() => {
    // Wait until the URL's language is active, or the head would get the old
    // language's strings for a moment and react-snap might save those.
    if (i18n.language !== lang.code) return;

    const title = t("seo.title");
    const description = t("seo.description");
    document.documentElement.lang = lang.hreflang;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:locale", lang.ogLocale);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("itemprop", "name", title);
    setMeta("itemprop", "description", description);

    removeAll('link[rel="canonical"]');
    removeAll('link[rel="alternate"][hreflang]');
    removeAll('meta[name="robots"]');
    if (indexable) {
      const url = `${site.siteUrl}${localizedPath(page, lang.code)}`;
      setMeta("property", "og:url", url);
      addLink("canonical", url);
      LANGUAGES.forEach((alt) => addLink("alternate", `${site.siteUrl}${localizedPath(page, alt.code)}`, alt.hreflang));
      addLink("alternate", `${site.siteUrl}${page}`, "x-default");
    } else {
      // Unknown paths still show the landing page so old links keep working, but
      // they must not compete with it in the index.
      setMeta("name", "robots", "noindex");
    }
  }, [t, i18n.language, lang, page, indexable]);

  return null;
}
